import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ApiError, api } from "@/lib/api";
import { formatDate } from "@/lib/i18n";
import { Link, Navigate, useLoaderData } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DismissWithReason } from "./dismiss-with-reason";
import { AutoDecidedFilter, LearningOutcome, type LearningOutcomeFields } from "./learning-outcome";

/**
 * `/agents/reviewer/learnings`: what the look back at Radim's meetings proposed.
 * Only an Active rule reaches Radim; a bug goes to Metoděj as a Hub ticket. A Word
 * (one his speech recognition got wrong) is active right away, Dismiss takes it out.
 */

type Status = "draft" | "active" | "dismissed" | "retire_proposed" | "expired";

type Learning = LearningOutcomeFields & {
  id: string;
  kind: "rule" | "bug" | "asr";
  text: string;
  evidence: string;
  evidenceCount: number;
  status: Status;
  /** Why it was dismissed: Hub's reason, or the note of whoever dismissed it. */
  resolvedNote: string | null;
  createdAt: string;
  convertedGlobalId: number | null;
  project: { id: string; name: string } | null;
  session: {
    id: string;
    startedAt: string;
    mode: string;
    // Null for a planner conversation across all projects.
    project: { slug: string; name: string } | null;
  } | null;
  reviewedBy: { id: string; name: string | null } | null;
};

const TABS = ["draft", "active", "retire_proposed", "expired", "dismissed", "all"] as const;
type Tab = (typeof TABS)[number];

export function ReviewerLearningsPage() {
  const { t } = useTranslation();
  const { user } = useLoaderData({ from: "/_app" });
  const [tab, setTab] = useState<Tab>("draft");
  const [rows, setRows] = useState<Learning[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [editing, setEditing] = useState<Record<string, string>>({});
  const [mineMessage, setMineMessage] = useState<string | null>(null);
  const [autoOnly, setAutoOnly] = useState(false);

  const load = useCallback(
    (which: Tab) => {
      setRows(null);
      const query = new URLSearchParams({
        ...(which === "all" ? {} : { status: which }),
        ...(autoOnly ? { auto: "1" } : {}),
      }).toString();
      return api
        .get<{ learnings: Learning[] }>(`/api/reviewer-learnings${query ? `?${query}` : ""}`)
        .then((res) => setRows(res.learnings))
        .catch(() => setError(t("agentTabs.reviewerLearnings.loadFailed")));
    },
    [t, autoOnly],
  );

  useEffect(() => {
    if (user.globalRole !== "admin") return;
    void load(tab);
  }, [user.globalRole, tab, load]);

  async function save(
    id: string,
    body: { status?: Status; text?: string; note?: string | undefined },
  ) {
    setBusyId(id);
    setError(null);
    try {
      await api.patch(`/api/reviewer-learnings/${id}`, body);
      setEditing(({ [id]: _, ...rest }) => rest);
      await load(tab);
    } catch {
      setError(t("agentTabs.reviewerLearnings.saveFailed"));
    } finally {
      setBusyId(null);
    }
  }

  async function sendToMetodej(id: string) {
    setBusyId(id);
    setError(null);
    try {
      await api.post(`/api/reviewer-learnings/${id}/send-to-metodej`, {});
      await load(tab);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("agentTabs.reviewerLearnings.sendFailed"));
    } finally {
      setBusyId(null);
    }
  }

  async function mineNow() {
    setMineMessage(null);
    try {
      await api.post("/api/reviewer-learnings/look-back", {});
      setMineMessage(t("agentTabs.reviewerLearnings.lookBackStarted"));
    } catch (err) {
      setMineMessage(
        err instanceof ApiError && err.status === 503
          ? t("agentTabs.reviewerLearnings.aiNotConfigured")
          : t("agentTabs.reviewerLearnings.lookBackFailed"),
      );
    }
  }

  if (user.globalRole !== "admin") return <Navigate to="/" />;

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold">{t("agentTabs.reviewerLearnings.title")}</h2>
          <p className="text-sm text-muted-foreground">{t("agentTabs.reviewerLearnings.intro")}</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Button type="button" variant="outline" size="sm" onClick={() => void mineNow()}>
            {t("agentTabs.reviewerLearnings.lookBackNow")}
          </Button>
          {mineMessage && <p className="text-xs text-muted-foreground">{mineMessage}</p>}
        </div>
      </div>

      <div className="flex gap-1 border-b">
        {TABS.map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            data-testid={`reviewer-learnings-tab-${key}`}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${
              tab === key
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t(`agentTabs.reviewerLearnings.tabs.${key}`)}
          </button>
        ))}
        <AutoDecidedFilter checked={autoOnly} onChange={setAutoOnly} />
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}
      {rows && rows.length === 0 && (
        <p className="text-sm text-muted-foreground" data-testid="reviewer-learnings-empty">
          {t("agentTabs.reviewerLearnings.empty")}
        </p>
      )}

      {rows?.map((r) => {
        const draft = editing[r.id];
        return (
          <Card key={r.id} data-testid="reviewer-learning-card">
            <CardContent className="space-y-2 pt-4">
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <Badge variant={r.kind === "bug" ? "destructive" : "default"}>
                  {t(`agentTabs.reviewerLearnings.kind.${r.kind}`)}
                </Badge>
                <Badge variant="secondary">
                  {r.project ? r.project.name : t("agentTabs.reviewerLearnings.everyProject")}
                </Badge>
                <span>{t("agentTabs.reviewerLearnings.seen", { count: r.evidenceCount })}</span>
                {r.session?.project && (
                  <Link
                    to="/projects/$slug/reviews"
                    params={{ slug: r.session.project.slug }}
                    search={{ review: r.session.id }}
                    className="hover:underline"
                  >
                    ·{" "}
                    {t("agentTabs.reviewerLearnings.fromReview", {
                      mode: r.session.mode,
                      project: r.session.project.name,
                      date: formatDate(r.session.startedAt),
                    })}
                  </Link>
                )}
                {r.session && !r.session.project && (
                  <span>
                    ·{" "}
                    {t("agentTabs.reviewerLearnings.fromReviewAll", {
                      mode: r.session.mode,
                      date: formatDate(r.session.startedAt),
                    })}
                  </span>
                )}
                {r.convertedGlobalId && (
                  <span>
                    · {t("agentTabs.reviewerLearnings.sentAs", { id: r.convertedGlobalId })}
                  </span>
                )}
                {r.reviewedBy && r.status !== "draft" && (
                  <span>
                    ·{" "}
                    {t(
                      r.status === "active"
                        ? "agentTabs.reviewerLearnings.activatedBy"
                        : "agentTabs.reviewerLearnings.dismissedBy",
                      { name: r.reviewedBy.name },
                    )}
                  </span>
                )}
              </div>
              <LearningOutcome learning={r} />
              {r.status === "dismissed" && r.resolvedNote && (
                <p className="text-xs text-muted-foreground" data-testid="learning-resolved-note">
                  {r.reviewedBy
                    ? t("agentTabs.reviewerLearnings.why")
                    : t("agentTabs.reviewerLearnings.dismissedByHub")}
                  {r.resolvedNote}
                </p>
              )}
              {draft === undefined ? (
                <p className="text-sm">{r.text}</p>
              ) : (
                <textarea
                  className="w-full rounded-md border bg-background p-2 text-sm"
                  rows={3}
                  value={draft}
                  aria-label={t("agentTabs.reviewerLearnings.text")}
                  onChange={(e) => setEditing((prev) => ({ ...prev, [r.id]: e.target.value }))}
                />
              )}
              <details className="text-xs text-muted-foreground">
                <summary className="cursor-pointer">
                  {t("agentTabs.reviewerLearnings.whySummary")}
                </summary>
                <p className="mt-1 whitespace-pre-wrap">{r.evidence}</p>
              </details>
              <div className="flex flex-wrap gap-2">
                {draft !== undefined ? (
                  <>
                    <Button
                      type="button"
                      size="sm"
                      disabled={busyId === r.id || !draft.trim()}
                      onClick={() => void save(r.id, { text: draft })}
                    >
                      {t("agentTabs.reviewerLearnings.save")}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditing(({ [r.id]: _, ...rest }) => rest)}
                    >
                      {t("agentTabs.reviewerLearnings.cancel")}
                    </Button>
                  </>
                ) : (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditing((prev) => ({ ...prev, [r.id]: r.text }))}
                  >
                    {t("agentTabs.reviewerLearnings.edit")}
                  </Button>
                )}
                {r.kind === "bug" && !r.convertedGlobalId && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={busyId === r.id}
                    onClick={() => void sendToMetodej(r.id)}
                  >
                    {t("agentTabs.reviewerLearnings.sendToMetodej")}
                  </Button>
                )}
                {r.kind !== "bug" && r.status !== "active" && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={busyId === r.id}
                    onClick={() => void save(r.id, { status: "active" })}
                  >
                    {t("agentTabs.reviewerLearnings.activate")}
                  </Button>
                )}
                {r.status !== "dismissed" && (
                  <DismissWithReason
                    busy={busyId === r.id}
                    onDismiss={(note) => void save(r.id, { status: "dismissed", note })}
                  />
                )}
                {r.status !== "draft" && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={busyId === r.id}
                    onClick={() => void save(r.id, { status: "draft" })}
                  >
                    {t("agentTabs.reviewerLearnings.backToDraft")}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
